"use client";

import { useConsent } from "@shared/hooks/cookie-consent";
import {
	clearCursorPreference,
	readCursorPreference,
	writeCursorPreference,
} from "@shared/lib/consent/preferences-consent-storage";
import {
	createContext,
	type PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

export type CursorStylePreference = "browser" | "custom";

interface CursorPreferenceContextValue {
	/** Persisted user preference (not affected by temporary suppress scopes). */
	style: CursorStylePreference;
	/**
	 * Style after applying temporary suppress scopes. Prefer this for mounting
	 * custom cursor chrome; keep `style` for preference UI / storage.
	 */
	effectiveStyle: CursorStylePreference;
	setStyle: (next: CursorStylePreference) => void;
	hydrated: boolean;
	/** True when one or more suppress scopes are active. */
	isCustomCursorSuppressed: boolean;
	/**
	 * Temporarily force the browser cursor for a named scope without changing
	 * the stored preference. Idempotent; pair with {@link releaseCustomCursorSuppress}.
	 */
	suppressCustomCursor: (scopeId: string) => void;
	releaseCustomCursorSuppress: (scopeId: string) => void;
}

const CursorPreferenceContext =
	createContext<CursorPreferenceContextValue | null>(null);

export function CursorPreferenceProvider({ children }: PropsWithChildren) {
	const { hasConsent, status } = useConsent();
	const preferencesAllowed =
		status === "decided" && hasConsent("preferences");
	const previousPreferencesAllowedRef = useRef(preferencesAllowed);
	const [style, setStyleState] = useState<CursorStylePreference>("browser");
	const [hydrated, setHydrated] = useState(false);
	const [suppressScopes, setSuppressScopes] = useState<ReadonlySet<string>>(
		() => new Set(),
	);

	useEffect(() => {
		if (preferencesAllowed) {
			const stored = readCursorPreference();
			if (stored != null) {
				setStyleState(stored);
			}
		} else if (previousPreferencesAllowedRef.current) {
			clearCursorPreference();
			setStyleState("browser");
		}
		previousPreferencesAllowedRef.current = preferencesAllowed;
		setHydrated(true);
	}, [preferencesAllowed]);

	const setStyle = useCallback(
		(next: CursorStylePreference) => {
			setStyleState(next);
			if (preferencesAllowed) {
				writeCursorPreference(next);
			}
		},
		[preferencesAllowed],
	);

	const suppressCustomCursor = useCallback((scopeId: string) => {
		const id = scopeId.trim();
		if (id.length === 0) {
			return;
		}
		setSuppressScopes((prev) => {
			if (prev.has(id)) {
				return prev;
			}
			const next = new Set(prev);
			next.add(id);
			return next;
		});
	}, []);

	const releaseCustomCursorSuppress = useCallback((scopeId: string) => {
		const id = scopeId.trim();
		if (id.length === 0) {
			return;
		}
		setSuppressScopes((prev) => {
			if (!prev.has(id)) {
				return prev;
			}
			const next = new Set(prev);
			next.delete(id);
			return next;
		});
	}, []);

	const isCustomCursorSuppressed = suppressScopes.size > 0;
	const effectiveStyle: CursorStylePreference =
		style === "custom" && !isCustomCursorSuppressed ? "custom" : "browser";

	const value = useMemo(
		() => ({
			style,
			effectiveStyle,
			setStyle,
			hydrated,
			isCustomCursorSuppressed,
			suppressCustomCursor,
			releaseCustomCursorSuppress,
		}),
		[
			style,
			effectiveStyle,
			setStyle,
			hydrated,
			isCustomCursorSuppressed,
			suppressCustomCursor,
			releaseCustomCursorSuppress,
		],
	);

	return (
		<CursorPreferenceContext.Provider value={value}>
			{children}
		</CursorPreferenceContext.Provider>
	);
}

export function useCursorPreference(): CursorPreferenceContextValue {
	const ctx = useContext(CursorPreferenceContext);
	if (ctx == null) {
		throw new Error(
			"useCursorPreference must be used within CursorPreferenceProvider",
		);
	}
	return ctx;
}

/**
 * While `active`, forces the browser cursor for `scopeId` without writing the
 * preference. Safe to call from overlays/modals; multiple scopes stack.
 */
export function useSuppressCustomCursor(
	scopeId: string,
	active: boolean,
): void {
	const { suppressCustomCursor, releaseCustomCursorSuppress } =
		useCursorPreference();

	useEffect(() => {
		const id = scopeId.trim();
		if (!active || id.length === 0) {
			return;
		}
		suppressCustomCursor(id);
		return () => {
			releaseCustomCursorSuppress(id);
		};
	}, [
		active,
		scopeId,
		suppressCustomCursor,
		releaseCustomCursorSuppress,
	]);
}
